import { Observer, observer } from 'mobx-react';
import React from 'react';
import './ceCollection.less';
import { FormBuilderContext } from '../formBuilderContext';
import { Configuration, Property } from '../models';
import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { GridStore } from 'controls/grid/gridStore';
import { FormBuilderStore } from '../formBuilderStore';
import { ArrayDataProvider } from 'controls/grid/arrayDataProvider';
import { useStore } from 'core/react/useStore';
import { Grid } from 'controls/grid/grid';
import { GridConfiguration, GridSelectionType } from 'controls/grid/gridConfiguration';
import { flatTree } from 'tools/tree';
import { MobxManager } from 'framework/mobx-integration';
import { YesNoEditor, yesNoRenderer } from 'controls/grid/renderers/yesNoRenderer';
import { GridMenuPlugin } from 'controls/grid/plugins/grid-menu/gridMenuPlugin';
import { GridMenuActionArgs } from 'controls/grid/plugins/grid-menu/gridMenuAction';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { ModalDescription, openModal } from 'controls/react/ant/antModal';
import { FormBuilder } from '../formBuilder';
import { Section } from 'controls/react/layout/section';
import { Toolbar } from 'controls/react/layout/toolbar';
import { GridMenuButton } from 'controls/grid/plugins/grid-menu/gridMenuButton';
import { isOneOf } from 'tools/utils';
import { renderProperty } from 'controls/react/form/formBuilderNew/components';
import { GridColumnConfig } from 'controls/grid/gridColumnConfig';
import { AntAlert } from 'controls/react/ant/antAlert';
import { capitalize, cloneDeep, get } from 'lodash';
import { newGuid } from 'tools/guid';
import { ActionButtons } from '../../actionButtons';
import b_ from 'b_';
import { UserSettingsStateProvider } from 'controls/grid/stateProviders';
import { AntButton } from 'controls/react/ant/antButton';

import { i18n } from 'core/localization';
const b = b_.with('ce-collection');

interface CECollectionProperty extends Property {
	inline: boolean;
	popup: boolean;
	immutable?: boolean;
	fields: string[];
	defaultConfiguration: Configuration;
	configurations: Configuration[];
}

interface CollectionItem {
	id: string;
	[key: string]: unknown;
}

type ExtendedGridColumnConfig = GridColumnConfig<CollectionItem> & {
	property: Property;
};

class CECollectionStoreBase {
	gridStore: GridStore<CollectionItem>;

	mobx: MobxManager = new MobxManager();

	constructor(
		public formStore: FormBuilderStore,
		public property: CECollectionProperty,
	) {
		makeObservable(this, {
			gridStore: observable,
			property: observable,
			createChild: action,
			rootValue: computed,
		});

		this.gridStore = new GridStore<CollectionItem>(this.getGridStoreConfig());

		this.mobx.reaction(
			() => toJS(this.rootValue),
			() => {
				this.gridStore.arrayDataProvider.updateData(toJS(this.rootValue));
				this.gridStore.dataProvider.silentReload();
				this.cleanupStores();
			},
			{ fireImmediately: true },
		);

		if (this.property.immutable) {
			this.mobx.reaction(
				() => this.formStore.dataSources.get(this.property.id) as Configuration[],
				(configurations) => {
					if (!configurations || !configurations.length) return;

					this.property.configurations = configurations || [];
					const value = configurations.map((x) => x.values);
					this.formStore.onPropertyValueChange(this.property, value);
					this.gridStore = new GridStore<CollectionItem>(this.getGridStoreConfig());
				},
			);
		}
	}

	cleanupStores() {}

	getGridStoreConfig() {
		const config = {
			columns: this.buildColumns(),
			dataProvider: new ArrayDataProvider([]),
			selection: GridSelectionType.None,
			plugins: [this.getGridMenuPlugin()],
			customization: {
				height: 'by-content',
			},
		} as GridConfiguration<CollectionItem>;

		if (this.formStore.userSettingsCategory) {
			const path = this.formStore.path.replaceAll(/\[\d+\]/g, '');
			config.stateProviders = [
				new UserSettingsStateProvider({
					category: [this.formStore.userSettingsCategory, path, this.property.id].filter((x) => x).join('-'),
				}),
			];
		}

		return config;
	}

	// todo: check what to do when we have no items
	get configuration() {
		return this.property.defaultConfiguration || this.property.configurations?.[0];
	}

	propToColumn = (property: Property) => {
		return {
			title: property.caption,
			field: property.id,
			renderer: this.getColumnRenderer(property),
			property,
		};
	};

	buildColumns(): ExtendedGridColumnConfig[] {
		const properties = flatTree(this.configuration?.properties || [], (x) => x.properties).filter(
			(x) => x.type !== 'CEGroup' || x.switchable,
		);
		if (this.property.fields && this.property.fields.length > 0) {
			return this.property.fields
				.map((field) => {
					const prop = properties.find((prop) => prop.id === field);
					if (!prop) {
						console.warn('No property found for field', field);
						return null;
					}
					return prop;
				})
				.filter((x) => x)
				.map(this.propToColumn);
		}
		return properties.map(this.propToColumn);
	}

	getGridMenuPlugin() {
		return new GridMenuPlugin<CollectionItem>({
			actions: [
				{
					id: 'create',
					icon: <PlusOutlined />,
					label: i18n('Create'),
					action: this.createChild,
				},
				{
					id: 'edit',
					icon: <EditOutlined />,
					label: i18n('Edit'),
					action: this.showEditForm,
					enabled: (selection) => selection.isOnlyOneSelected,
				},
				{
					id: 'delete',
					icon: <DeleteOutlined />,
					label: i18n('Delete'),
					action: this.deleteBySelection,
					confirmation: {
						singularQuestion: i18n('Delete selected item?'),
						pluralQuestion: i18n('Delete selected items?'),
					},
					enabled: (selection) => selection.isAnythingSelected,
					enableSuccessNotification: true,
				},
			],
			mainMenu: this.mainMenu,
			inlineActions: this.inlineActionsColumn ? { actions: this.inlineActionsColumn } : undefined,
		});
	}

	createChild = () => {
		const configuration = observable(cloneDeep(this.property.defaultConfiguration));
		const id = newGuid();
		const value = configuration.values as CollectionItem;
		value.id = id;

		this.rootValue.push(value);
		this.afterCreate(value.id);
	};

	get mainMenu() {
		return [
			!this.property.immutable && 'create',
			this.property.popup && 'edit',
			!this.property.immutable && 'delete',
		]
			.filter(Boolean)
			.join(' ');
	}
	get inlineActionsColumn() {
		return [this.property.popup && 'edit', !this.property.immutable && 'delete'].filter(Boolean).join(' ');
	}

	deleteBySelection = ({ selection }: GridMenuActionArgs<CollectionItem>) => {
		const ids = selection.dataset.items.map((x) => x.data.id);
		ids.forEach((id) => this.deleteById(id));
	};

	deleteById = (id: string) => {
		this.rootValue.splice(
			this.rootValue.findIndex((x) => x.id == id),
			1,
		);
	};

	createSubFormStore(id: string, inline: boolean = false) {
		const index = this.rootValue.findIndex((x) => x.id == id);
		let configuration = this.property.configurations.find((x) => x.id == id);

		if (!configuration) {
			configuration = observable(cloneDeep(this.configuration));
			configuration.id = id;
			this.property.configurations.push(configuration);
		}

		const subFormStore = new FormBuilderStore({
			configuration,
			path: this.path + `[${index}]`,
			rootFormStore: this.formStore.rootStore,
			mode: this.formStore.mode,
			columnsCount: 1,
			inline,
		});

		return subFormStore;
	}

	showEditForm = ({ selection }: GridMenuActionArgs<CollectionItem>) => {
		this.formStore.lock();
		const id = selection.dataset.items[0].id;
		const index = this.rootValue.findIndex((x) => x.id == id);

		const subFormStore = this.createSubFormStore(id);

		const previousValue = cloneDeep(this.rootValue[index]);

		const onCancel = () => {
			this.rootValue[index] = previousValue;
			subFormStore.value = previousValue;
			this.formStore.unlock();
		};

		this.openModal(subFormStore, 'edit', onCancel);
	};

	afterCreate = (id: string) => {
		this.formStore.lock();
		const subFormStore = this.createSubFormStore(id);
		const onCancel = () => {
			this.deleteById(id);
			this.formStore.unlock();
		};
		this.openModal(subFormStore, 'create', onCancel);
	};

	openModal(store: FormBuilderStore, mode: 'edit' | 'create', onCancel: () => void) {
		const modal = openModal(
			{
				width: 800,
				onCancel,
				resizable: true,
				draggable: true,
				footer: (
					<Observer>
						{() => {
							return this.actionButtons(mode, store, modal, onCancel);
						}}
					</Observer>
				),
			},
			<Section title={i18n(capitalize(mode))} contentClass={b('modal-content')}>
				<FormBuilder store={store} />
			</Section>,
		);
	}

	private actionButtons(mode: string, subFormStore: FormBuilderStore, modal: ModalDescription, onCancel: () => void) {
		return (
			<Toolbar>
				<ActionButtons
					validator={subFormStore.validator.modelValidator}
					mode={mode}
					onSave={() => {
						modal.close();
						this.refreshGrid();
						this.formStore.unlock();
					}}
					disabled={subFormStore.locked}
					onCancel={() => {
						modal.close();
						onCancel();
					}}
					actionButtonLabel={mode == 'create' ? i18n('Create') : i18n('Update')}
					fieldLabel={(field) => subFormStore.propertyLabel(field)}
				/>
			</Toolbar>
		);
	}

	refreshGrid = () => {
		this.gridStore.arrayDataProvider.updateData(this.rootValue);
	};

	get rootValue(): CollectionItem[] {
		return get(this.formStore.rootValue, this.path) as CollectionItem[];
	}

	get path() {
		return [this.formStore.path, `${this.property.id}`].filter((x) => x).join('.');
	}

	itemPropTextField(item: CollectionItem, prop: Property) {
		const allProperties = flatTree(
			this.property.configurations.find((x) => x.id == item.id)?.properties || [],
			(x) => x.properties,
		);

		return allProperties.find((x) => x.id == prop.id)?.text;
	}

	getColumnRenderer(prop: Property) {
		switch (prop.type) {
			case 'CECollection':
				return (item: CollectionItem) => (item[prop.id] as CollectionItem[]).length;
			case 'CECheckBox':
			case 'CEGroup':
				return (item: CollectionItem) => yesNoRenderer(item[prop.id] as boolean);
			default:
				return (item: CollectionItem) => this.itemPropTextField(item, prop) || item[prop.id] || '';
		}
	}

	get errors(): { property: string; error: string }[] {
		return [];
	}

	destroy() {
		this.mobx.destroy();
	}
}

class CECollectionStore extends CECollectionStoreBase {
	constructor(formStore: FormBuilderStore, property: CECollectionProperty) {
		super(formStore, property);
		makeObservable(this, {});
	}
}

class CECollectionInlineStore extends CECollectionStoreBase {
	childrenFormBuilderStores: Map<string, FormBuilderStore> = new Map();
	constructor(formStore: FormBuilderStore, property: CECollectionProperty) {
		super(formStore, property);
		this.gridStore.config.customization = {
			height: 'by-content',
			styling: {
				defaultRowHeight: 60,
			},
		};
		makeObservable(this, {
			childrenFormBuilderStores: observable,
			errors: computed,
		});
	}

	getColumnEditor(prop: Property) {
		switch (prop.type) {
			case 'CELabel':
				return (item: CollectionItem) => item[prop.id];
			case 'CECollection':
				return (item: CollectionItem) => (item[prop.id] as CollectionItem[]).length;
			case 'CECheckBox':
			case 'CEGroup':
				return (item: CollectionItem) => {
					const subStore = this.getChildFormBuilderStore(item.id);
					const value = subStore.value as { [prop.id]: boolean };
					return <YesNoEditor disabled={subStore.propertyDisabled(prop)} model={value} field={prop.id} />;
				};
			default:
				return (item: CollectionItem) => {
					return (
						<FormBuilderContext.Provider value={this.getChildFormBuilderStore(item.id)}>
							<div style={{ width: '100%' }}>{renderProperty(prop, this.formStore)}</div>
						</FormBuilderContext.Provider>
					);
				};
		}
	}

	cleanupStores() {
		for (const id of [...this.childrenFormBuilderStores.keys()]) {
			if (!this.rootValue.some((x) => x.id == id)) {
				this.deleteById(id);
			}
		}
	}

	deleteById = (id: string) => {
		const index = this.rootValue.findIndex((x) => x.id == id);
		if (index >= 0) {
			this.rootValue.splice(index, 1);
		}

		const subStore = this.childrenFormBuilderStores.get(id);
		if (subStore) {
			this.formStore.removeSubStore(this.property, subStore);
			this.childrenFormBuilderStores.delete(id);
		}
	};

	getChildFormBuilderStore(id: string) {
		let formStore = this.childrenFormBuilderStores.get(id);
		if (!formStore) {
			formStore = this.createSubFormStore(id, true);

			this.formStore.registerSubStore(this.property, formStore);
			this.childrenFormBuilderStores.set(id, formStore);
		}
		return formStore;
	}

	buildColumns() {
		const columns = super.buildColumns();
		columns.forEach((x) => {
			if (this.getColumnEditable(x.property)) {
				x.renderer = this.getColumnEditor(x.property);
				if (x.property.type == 'CESelection') {
					x.minWidth = 200;
				}
			}
		});
		return columns;
	}

	getColumnEditable(x: Property) {
		if (isOneOf(x.type, ['CECollection'])) {
			return false;
		}
		if (x.type == 'CEGroup' && !x.switchable) {
			return false;
		}
		return true;
	}

	get errors() {
		const stores = [...this.childrenFormBuilderStores.values()];
		return stores
			.flatMap((x) => [...x.validator.loadErrors.entries()])
			.map(([property, error]) => ({ property, error }));
	}

	afterCreate = (id: string) => {
		const subStore = this.createSubFormStore(id, true);
		this.formStore.registerSubStore(this.property, subStore);
		this.childrenFormBuilderStores.set(id, subStore);
		this.gridStore.arrayDataProvider.updateData(this.rootValue);
	};
}

export const CECollection = observer(({ property }: { property: CECollectionProperty }) => {
	const formStore = React.useContext(FormBuilderContext);
	const store = useStore(() => {
		if (property.inline) {
			return new CECollectionInlineStore(formStore, property);
		}
		return new CECollectionStore(formStore, property);
	});

	return (
		<Section appearance="card" childrenPadding disabled={formStore.propertyDisabled(property)}>
			<Toolbar title={property.caption}>
				{store.mainMenu && <GridMenuButton store={store.gridStore} />}
				<AntButton
					icon={<ReloadOutlined />}
					size={'small'}
					title={i18n('Reset to Defaults')}
					onClick={() => store.gridStore.state.reset(store.gridStore)}
				/>
			</Toolbar>
			{property.inline && store.errors.length > 0 && (
				<AntAlert
					type="error"
					className={b('errors')}
					message={
						<div>
							{store.errors.map((x) => (
								<div key={x.property}>
									{x.property}: {x.error}
								</div>
							))}
						</div>
					}
				/>
			)}
			<Grid store={store.gridStore} />
		</Section>
	);
});
